/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { JwtAccessPayload } from "util/moduleAccess";
import { BellRing, X } from "lucide-react";
import { SubscriptionStatus } from "./useSubscriptionStatus";

interface PlanNotificationBannerProps {
    payload: JwtAccessPayload | null;
    /**
     * Live subscription status for the organisation in session, owned by the
     * shell (see useSubscriptionStatus). NOTE: the banner used to fetch this
     * itself; sharing the shell's copy means the banner and the account menu's
     * "Upgrade Plan" entry can never disagree about the current plan.
     */
    status: SubscriptionStatus | null;
    /** Active organisation, used to reset the dismissal when it changes. */
    activeSubdomain?: string;
    onVisibilityChange?: (isVisible: boolean) => void;
}

export default function PlanNotificationBanner({
    payload,
    status,
    activeSubdomain = "",
    onVisibilityChange,
}: PlanNotificationBannerProps) {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(true);

    // Check if the user is an Admin using ROLE_ADMIN
    const userRole = (payload?.role || "").toString().trim().toUpperCase();
    const isAdmin = userRole === "ROLE_ADMIN";

    const lastSeenOrgRef = useRef<string | null>(null);

    /**
     * A different organisation means a different subscription, so a dismissal
     * made for the previous one must not keep the new banner hidden.
     */
    useEffect(() => {
        if (lastSeenOrgRef.current !== null && lastSeenOrgRef.current !== activeSubdomain) {
            setIsVisible(true);
        }
        lastSeenOrgRef.current = activeSubdomain;
    }, [activeSubdomain]);

    useEffect(() => {
        if (onVisibilityChange) {
            onVisibilityChange(isVisible);
        }
    }, [isVisible, onVisibilityChange]);

    // Plan drives the wording and the target route; API value wins, JWT is the fallback
    const planType = (
        status?.plan ||
        (status as any)?.planType ||
        payload?.plan ||
        payload?.planType ||
        ""
    )
        .toString()
        .trim()
        .toUpperCase();
    const isTrialPlan = planType === "TRIAL";
    const isEnterprisePlan = planType === "ENTERPRISE";

    const daysLeft = useMemo(() => {
        /**
         * The banner shows `daysLeft` straight from api/subscription/status.
         * Number() rather than a typeof check, so a numeric string ("3") is
         * accepted as well; anything unusable falls through to the JWT-derived
         * count below.
         */
        const apiDaysLeft = status?.daysLeft ?? (status as any)?.daysRemaining;
        if (apiDaysLeft !== null && apiDaysLeft !== undefined && String(apiDaysLeft).trim() !== "") {
            const parsedDaysLeft = Number(apiDaysLeft);
            if (Number.isFinite(parsedDaysLeft)) {
                return Math.max(0, Math.ceil(parsedDaysLeft));
            }
        }

        const subscriptionEnd = (status as any)?.subscriptionEnd ?? (payload as any)?.subscriptionEnd;

        if (subscriptionEnd) {
            const endDate = new Date(subscriptionEnd).getTime();
            const now = Date.now();
            const diff = endDate - now;

            if (diff <= 0) return 0;
            return Math.ceil(diff / (1000 * 60 * 60 * 24)); // Milliseconds to days
        }

        // Fallback to legacy 'exp' if subscriptionEnd is missing
        if (payload?.exp) {
            const now = Math.floor(Date.now() / 1000);
            const diff = payload.exp - now;

            if (diff <= 0) return 0;
            return Math.ceil(diff / (60 * 60 * 24)); // Seconds to days
        }

        return null;
    }, [payload, status]);

    // The API is the single source of truth for showing the banner
    const showBanner = status?.showBanner === true;

    // Day count is optional now, so the phrase degrades to "soon" without one
    const expiryPhrase =
        daysLeft !== null ? `in ${daysLeft} ${daysLeft === 1 ? "day" : "days"}` : "soon";

    const user = localStorage.getItem("user");

    // --- NEW: Centralized handler to pass required state and route conditionally ---
    const handleUpgradeClick = () => {
        // Extract ID, Plan, and Schema Name from the JWT payload
        const userId = user ? JSON.parse(user).id : null;
        const currentPlan = payload?.plan || payload?.planType || "";
        const schemaName = (payload as any)?.tenant || "";

        // 1. Set Session Storage fallbacks (expected by downstream components)
        if (userId) sessionStorage.setItem("userId", userId.toString());
        if (currentPlan) sessionStorage.setItem("plan", currentPlan.toString());
        if (schemaName) sessionStorage.setItem("schemaName", schemaName.toString());

        const navigationState = {
            id: userId,
            plan: currentPlan,
            schemaName: schemaName
        };

        // 2. Navigate based on Plan Type
        if (isEnterprisePlan) {
            navigate('/pending-invoices', { state: navigationState });
        } else {
            navigate('/plan-selection', { state: navigationState });
        }
    };

    // Render nothing if the user is NOT an admin, the API did not ask for the
    // banner, OR the user closed it
    if (!isAdmin || !showBanner || !isVisible) {
        return null;
    }

    // Determine the text to display: Use API message if available, otherwise fallback to default text
    const displayMessage = (status as any)?.message || (
        isTrialPlan
            ? `You have a Trial Plan. Which will expire ${expiryPhrase}. Please upgrade your plan to avoid interruption.`
            : isEnterprisePlan
                ? `Hi, your plan will expire ${expiryPhrase}. Renew your plan now, or contact support for assistance.`
                : `Hi, your plan will expire ${expiryPhrase}. Renew your plan now, or upgrade to another featured plan.`
    );

    // Determine button label
    const buttonText = isTrialPlan
        ? "Upgrade Plan"
        : isEnterprisePlan
            ? "Renew Plan"
            : "Renew Plan / Upgrade Plan";

    return (
        <div className="hidden lg:flex flex-1 justify-start ml-4 xl:ml-8">
            {/* Custom inline animation for the ringing bell */}
            <style>
                {`
                    @keyframes ring-animation {
                        0%, 100% { transform: rotate(0deg); }
                        10% { transform: rotate(15deg); }
                        20% { transform: rotate(-10deg); }
                        30% { transform: rotate(15deg); }
                        40% { transform: rotate(-10deg); }
                        50%, 100% { transform: rotate(0deg); }
                    }
                    .animate-ring {
                        animation: ring-animation 2s ease-in-out infinite;
                        transform-origin: top center;
                    }
                `}
            </style>

            <div className="bg-orange-50 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 text-[11.5px] xl:text-xs font-medium tracking-tight rounded-lg border border-orange-200 dark:border-orange-800/50 py-1.5 px-3 shadow-sm flex items-center gap-1.5 transition-all duration-300 whitespace-nowrap">
                {/* Ringing Bell Icon */}
                <BellRing className="w-3.5 h-3.5 animate-ring text-orange-600 dark:text-orange-400 shrink-0" />

                <div className="flex items-center">
                    <span>
                        {displayMessage}{" "}
                        <button
                            onClick={handleUpgradeClick}
                            className="underline font-bold hover:text-orange-900 dark:hover:text-orange-100 ml-1 transition-colors"
                        >
                            {buttonText}
                        </button>
                    </span>
                </div>

                {/* Close Button */}
                <button
                    onClick={() => setIsVisible(false)}
                    className="ml-1.5 p-0.5 rounded-md hover:bg-orange-200 dark:hover:bg-orange-800/50 text-orange-600 dark:text-orange-400 transition-colors shrink-0 outline-none"
                    title="Dismiss alert"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}